import Loader from "Components/Common/Loader";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchSocialAccountsThunk } from "slices/auth/thunk";
import { fetchSocialAccountTrackedPostsThunk } from "slices/socialAccounts/thunk";
import { RootState, AppDispatch } from "store";

import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import "./style.scss";
import SocialPostModal from "Components/social-account/SocialPostModal";
import { closeSocialPostModal } from "slices/ui/reducer";
import AddToCampaignOffcanvas from "Components/social-account/AddToCampaignOffcanvas";
import { PostTypeOptionLabel, PostTypeOptions, ReferenceType, ReferenceTypeLabel, SortOptionLabel, SortOptions, TrackedPost, TrackedPostsFilters } from "types/social-account";
import { attachTrackedPostToCampaign, refreshTrackedMedia } from "services/api/social-account";
import { toast } from "react-toastify";
import { generateUniqueKey, getErrorMessage } from "utils/helpers";
import { cleanTrackedPostState, postAddedToCampaign, setTrackedPostsFilters } from "slices/socialAccounts/reducer";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { DropdownItem, DropdownMenu, DropdownToggle, Nav, NavItem, NavLink, Tooltip, UncontrolledDropdown } from "reactstrap";
import Flatpickr from 'react-flatpickr';
import moment from 'moment';
import userDummyImg from "../../../assets/images/users/user-dummy-img.jpg"
import TrackedPostCard from "Components/social-account/TrackedPostCard";
import SocialAccountCreators from "Components/social-account/SocialAccountCreators";
import CreatorsFilters from "Components/social-account/SocialAccountCreators/Filters";
import ContentMasonry from "Components/social-account/ContentMasonry";
import { Collaboration } from "types/campaign/collaborations";
import { markFeaturesDataStale } from "slices/dashboard/reducer";
import ifConfigured from "hoc/ifConfigured";
const TrackedPosts = () => {
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const socialAccounts = useSelector((state: RootState) => state.auth.social_accounts.items);
    const [socialAccountsLoading, setSocialAccountsLoading] = useState(true);
    const filters = useSelector((state: RootState) => state.socialAccountState.trackedPosts.filters);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [flatpickr, setFlatPickr] = useState<boolean>(true)
    const [activeTab, setActiveTab] = useState<string>('content');

    const [searchParams] = useSearchParams();
    const socialAccountIdParam = searchParams.get('social_account_id');
    const [socialAccountId, setSocialAccountId] = useState<string | null>(socialAccountIdParam ?? null);
    const location = useLocation();
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));


    const loading = useSelector((state: RootState) => state.socialAccountState.loading);
    const error = useSelector((state: RootState) => state.socialAccountState.error);


    const trackedPostsData = useSelector((state: RootState) => state.socialAccountState.trackedPosts.items[socialAccountId!]?.filtered_lists?.[filterId!]);
    const trackedPosts = trackedPostsData?.items ?? [];
    const has_more = trackedPostsData?.has_more ?? true;
    const page = trackedPostsData?.page ?? 1;

    const [selectedPost, setSelectedPost] = useState<null | TrackedPost>(null);

    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshTooltipOpen, setRefreshTooltipOpen] = useState(false);



    const dispatch: AppDispatch = useDispatch();

    useEffect(() => {
        setSocialAccountsLoading(true)
        dispatch(fetchSocialAccountsThunk({ force: false })).finally(() => setSocialAccountsLoading(false));
    }, [dispatch]);

    const eligibleSocialAccounts = useMemo(() => {
        return socialAccounts?.filter((a) => a.track_media?.status === 'active');
    }, [socialAccounts])

    useEffect(() => {
        if (!socialAccountId && eligibleSocialAccounts[0]) {
            setSocialAccountId(eligibleSocialAccounts[0].social_account.uuid);
        }
    }, [eligibleSocialAccounts, socialAccountId])

    const selectedSocialAccount = useMemo(() => {
        return socialAccounts.find((a) => a.social_account.uuid === socialAccountId);
    }, [socialAccounts, socialAccountId])

    useEffect(() => {
        if (socialAccountId && filters) {
            if (filters.page && filters.page === 1) {
                setInitialLoading(true);
            }
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            debounceTimeout.current = setTimeout(() => {
                dispatch(fetchSocialAccountTrackedPostsThunk({ socialAccountId, filters })).finally(() => {
                    setInitialLoading(false);
                    setFilterId(generateUniqueKey(filters, ['page']))
                })
            }, 500)
        }
    }, [socialAccountId, filters])

    const nextPage = () => {
        if (has_more && socialAccountId && !loading) {
            const nextPageNumber = page + 1;
            dispatch(fetchSocialAccountTrackedPostsThunk({ filters: { ...filters, page: nextPageNumber }, socialAccountId }))
        }
    }

    const addToCampaign = async (campaign: { id: number; name: string, uuid: string }, cb: () => void) => {
        if (!selectedAgencyId || !selectedPost || !socialAccountId) return;
        try {
            await attachTrackedPostToCampaign(selectedAgencyId, selectedPost.social_post.uuid, campaign.id, socialAccountId);
            toast.success(`Tracked post added to campaign "${campaign.name}" successfully.`);
            dispatch(postAddedToCampaign({ socialAccountId, collaboration: { campaign: { id: campaign.id, uuid: campaign.uuid, name: campaign.name } }, trackedPostId: selectedPost.social_post.uuid }));
            dispatch(markFeaturesDataStale());
        } catch (error) {
            console.log("Error attaching ttracked post to campaign:", error);
            toast.error(getErrorMessage(error, "Failed to add tracked post to campaign."));
        } finally {
            cb();
        }
    }

    const setFilters = (updatedFilters: TrackedPostsFilters) => {
        dispatch(setTrackedPostsFilters(updatedFilters));
    }

    const selectedReferenceType = useMemo(() => {
        if (filters.is_tagged === 'y') return ReferenceType.TAGGED;
        if (filters.is_mentioned === 'y') return ReferenceType.MENTIONED;
        return ReferenceType.ALL;
    }, [filters])

    const handleFilterChange = (key: string, value: any) => {
        setFilters({ ...filters, [key]: value });
    };


    const getSelectedDate = () => {
        const { start_date, end_date } = filters;
        const dates: Date[] = [];
        if (start_date) dates.push(moment(start_date, 'YYYY-MM-DD').toDate());
        if (end_date) dates.push(moment(end_date, 'YYYY-MM-DD').toDate());
        return dates;
    };

    const handleDateChange = (selectedDates: Date[]) => {
        if (selectedDates.length === 2) {
            setFilters({ ...filters, start_date: moment(selectedDates[0]).format('YYYY-MM-DD'), end_date: moment(selectedDates[1]).format('YYYY-MM-DD') })
        } else if (selectedDates.length === 1) {
            // handleFilterChange('end_date', '');
        } else {
            let updated_filters = { ...filters };
            delete updated_filters.start_date;
            delete updated_filters.end_date;
            setFilters(updated_filters);
        }
    };

    const refreshAllowed = useMemo(() => {
        return !!selectedSocialAccount?.track_media?.refresh_allowed;
    }, [selectedSocialAccount])

    const refreshMedia = async () => {
        if (!selectedAgencyId || !socialAccountId || refreshing || !refreshAllowed) return;
        try {
            setRefreshing(true);
            await refreshTrackedMedia(selectedAgencyId, socialAccountId);
            await dispatch(fetchSocialAccountsThunk({ force: true }))
            toast.success("Tracked media will be refreshed soon!");
        } catch (error) {
            console.log('err', error);
            toast.error(getErrorMessage(error));
        } finally {
            setRefreshing(false);
        }
    }

    if (socialAccountsLoading) {
        return <Loader />
    }
    return (
        <>
            <div className="campaign-page">
                <div className="d-flex align-items-center justify-content-between gap-3">
                    <h1 className="campaign-page-title gap-2 d-flex align-items-center">Track Media
                        {
                            selectedSocialAccount &&
                            <span>
                                <button disabled={!refreshAllowed || refreshing} onClick={() => refreshMedia()} id="refreshButton" className="unset-all cursor-pointer">
                                    <span className={`mdi mdi-sync ${!refreshAllowed ? 'text-muted' : 'text-primary'} refresh-icon ${refreshing ? 'spinning' : ''}`}></span>
                                </button>
                                <Tooltip innerClassName="custom-tooltip" autohide={true} flip={true} isOpen={refreshTooltipOpen} toggle={() => setRefreshTooltipOpen(!refreshTooltipOpen)} target="refreshButton">
                                    <div>
                                        <i className="mdi mdi-calendar"></i> Last Fetched At: <b>{selectedSocialAccount?.track_media?.last_fetched ?? '-'}</b>
                                    </div>
                                </Tooltip>
                            </span>
                        }
                    </h1>
                    {
                        socialAccountId &&
                        <div className="d-flex gap-3 align-items-center justify-content-end">
                            {/* <UncontrolledDropdown className='sort-dropdown'>
                                <DropdownToggle tag={"button"} className='btn btn-white d-flex justify-content-between align-items-center w-100'>
                                    <span className="d-flex align-items-center">
                                        <span className='text-muted me-1'>Type: </span>
                                        {
                                            ReferenceTypeLabel[selectedReferenceType]
                                        }
                                    </span>
                                    <div className="d-flex gap-2 align-items-center justify-content-between">

                                        {
                                            selectedReferenceType === ReferenceType.ALL && <span> <IGMentionIcon size={16} /> <IGTagIcon size={16} />  </span>
                                        }
                                        {
                                            selectedReferenceType === ReferenceType.TAGGED && <span> <IGTagIcon size={16} />  </span>
                                        }
                                        {
                                            selectedReferenceType === ReferenceType.MENTIONED && <span> <IGMentionIcon size={16} /> </span>
                                        }
                                        <i className='mdi mdi-chevron-down fs-5' />

                                    </div>
                                </DropdownToggle>
                                <DropdownMenu className='mt-1 p-0 w-100'>
                                    {
                                        Object.values(ReferenceType).map((referenceType, i) => <DropdownItem key={i} onClick={() => {
                                            let updated_filters = { ...filters };
                                            if (referenceType === ReferenceType.MENTIONED) {
                                                updated_filters.is_mentioned = 'y';
                                                delete updated_filters.is_tagged;
                                            }
                                            else if (referenceType === ReferenceType.TAGGED) {
                                                updated_filters.is_tagged = 'y';
                                                delete updated_filters.is_mentioned;
                                            } else {
                                                delete updated_filters.is_tagged;
                                                delete updated_filters.is_mentioned;
                                            }
                                            setFilters(updated_filters);
                                        }}>
                                            <div className="d-flex gap-2 align-items-center justify-content-between">
                                                {ReferenceTypeLabel[referenceType]}
                                                {
                                                    referenceType === ReferenceType.ALL && <span> <IGMentionIcon size={16} /> <IGTagIcon size={16} />  </span>
                                                }
                                                {
                                                    referenceType === ReferenceType.TAGGED && <span> <IGTagIcon size={16} />  </span>
                                                }
                                                {
                                                    referenceType === ReferenceType.MENTIONED && <span> <IGMentionIcon size={16} /> </span>
                                                }
                                            </div>
                                        </DropdownItem>)
                                    }
                                </DropdownMenu>
                            </UncontrolledDropdown> */}
                            <UncontrolledDropdown className='sort-dropdown'>
                                <DropdownToggle tag={"button"} className='btn btn-white d-flex justify-content-between align-items-center w-100'>
                                    <span className="d-flex align-items-center gap-1">
                                        {
                                            selectedSocialAccount?.social_account?.profile_picture_url ? (
                                                <img src={selectedSocialAccount?.social_account?.profile_picture_url} className="rounded-circle avatar-xxs" alt="" />
                                            ) : <img src={userDummyImg} className="rounded-circle avatar-xxs" alt="" />
                                        }
                                        @{selectedSocialAccount?.social_account?.handle}
                                    </span>
                                    {
                                        (eligibleSocialAccounts).length > 0 &&
                                        <i className='mdi mdi-chevron-down fs-5' />
                                    }
                                </DropdownToggle>
                                <DropdownMenu className='mt-1 p-0 w-100'>
                                    {
                                        (socialAccounts ?? []).filter((a) => a.track_media?.status === 'active').map((socialAccount, i) => <DropdownItem key={i} onClick={() => setSocialAccountId(socialAccount.social_account.uuid)}>
                                            <span className="d-flex align-items-center gap-1">
                                                {
                                                    socialAccount?.social_account?.profile_picture_url ? (
                                                        <img src={socialAccount?.social_account?.profile_picture_url} className="rounded-circle avatar-xxs" alt="" />
                                                    ) : <img src={userDummyImg} className="rounded-circle avatar-xxs" alt="" />
                                                }
                                                @{socialAccount.social_account.handle}
                                            </span>
                                        </DropdownItem>)
                                    }
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </div>
                    }

                </div>
                <div className="d-flex justify-content-between align-items-center campaign-page-info flex-column flex-lg-row mt-3 gap-2 gap-lg-0">
                    <Nav tabs className="nav nav-tabs nav-tabs-custom nav-primary nav-justified align-items-center campaign-nav w-100 w-lg-50">
                        <NavItem className='campaign-nav-item'>
                            <NavLink
                                style={{ cursor: "pointer" }}
                                className={`campaign-nav-link ${activeTab === 'content' && 'active-campaign-nav'} text-start`}
                                onClick={() => setActiveTab('content')}
                            >
                                Content
                            </NavLink>
                        </NavItem>
                        <NavItem className='campaign-nav-item'>
                            <NavLink
                                style={{ cursor: "pointer" }}
                                className={`campaign-nav-lin ${activeTab === 'creators' && 'active-campaign-nav'} text-start`}
                                onClick={() => setActiveTab('creators')}
                            >
                                Creators
                            </NavLink>
                        </NavItem>
                    </Nav>
                    {
                        activeTab === 'content' && selectedSocialAccount &&
                        <div className="d-flex gap-3 align-items-center justify-content-end">
                            <div className="position-relative">
                                {
                                    (filters.start_date || filters.end_date) &&
                                    <button onClick={() => {
                                        handleDateChange([])
                                        setFlatPickr(false);
                                        setTimeout(() => {
                                            setFlatPickr(true)
                                        }, 0)
                                    }} className="unset-all text-primary cursor-pointer" style={{ fontSize: '12px', position: 'absolute', top: '6px', right: '6px' }}> <i className="mdi mdi-close fs-5 fw-bold"></i> </button>
                                }
                                {
                                    flatpickr &&
                                    <Flatpickr
                                        className="border-0 dash-filter-picker filter-date-range performance-date-range-picker form-control"
                                        options={{
                                            mode: 'range',
                                            dateFormat: 'd M, Y',
                                        }}
                                        style={{ width: '220px' }}
                                        {...({ placeholder: "Select Date Range" } as any)}
                                        value={getSelectedDate() as unknown as Date}
                                        onChange={handleDateChange}
                                    />
                                }
                            </div>

                            <UncontrolledDropdown className='sort-dropdown'>
                                <DropdownToggle tag={"button"} className='btn btn-white d-flex justify-content-between align-items-center w-100'>
                                    <span>
                                        <span className='text-muted me-1'>Post Type: </span>
                                        {
                                            (!filters.type || filters.type === PostTypeOptions.ALL) ? PostTypeOptionLabel[PostTypeOptions.ALL] : PostTypeOptionLabel[filters.type as keyof typeof PostTypeOptionLabel]
                                        }
                                    </span>
                                    <i className='mdi mdi-chevron-down fs-5' />
                                </DropdownToggle>
                                <DropdownMenu className='mt-1 p-0 w-100'>
                                    {
                                        Object.values(PostTypeOptions).map((postTypeOption, i) => <DropdownItem key={i} onClick={() => setFilters({ ...filters, ...(postTypeOption === PostTypeOptions.ALL ? { type: null } : { type: postTypeOption }) })}>{PostTypeOptionLabel[postTypeOption]}</DropdownItem>)
                                    }
                                </DropdownMenu>
                            </UncontrolledDropdown>
                            <UncontrolledDropdown className='sort-dropdown'>
                                <DropdownToggle tag={"button"} className='btn btn-white d-flex justify-content-between align-items-center w-100'>
                                    <span>
                                        <span className='text-muted me-1'>Sort by: </span>
                                        {SortOptionLabel[filters.sort as keyof typeof SortOptionLabel]}
                                    </span>
                                    <i className='mdi mdi-chevron-down fs-5' />
                                </DropdownToggle>
                                <DropdownMenu className='mt-1 p-0 w-100'>
                                    {
                                        Object.values(SortOptions).map((sortOption, i) => <DropdownItem key={i} onClick={() => setFilters({ ...filters, sort: sortOption })}>{SortOptionLabel[sortOption]}</DropdownItem>)
                                    }
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </div>
                    }
                    {
                        activeTab === 'creators' && selectedSocialAccount && <CreatorsFilters />
                    }
                </div>
                {
                    activeTab === 'content' &&
                    <div className="campaign-page-content my-3">
                        {
                            (socialAccountId && selectedSocialAccount) ? (
                                <>
                                    {
                                        selectedSocialAccount?.track_media?.status !== 'active' && trackedPosts.length > 0 &&
                                        <div className="alert alert-warning" role="alert">
                                            Media tracking is not enabled for the selected account. Please enable tracking from social accounts page. <Link to={{ pathname: '/social-accounts' }}>View Accounts</Link>
                                        </div>
                                    }
                                    <InfiniteScroll
                                        dataLength={trackedPosts.length + 1}
                                        next={nextPage}
                                        className=''
                                        hasMore={has_more}
                                        loader={<></>}
                                    >
                                        <ContentMasonry onAddToCampaignClick={(post) => {
                                            const selectedTrackedPost = trackedPosts.find((p) => p.social_post.uuid === post.uuid);
                                            if (selectedTrackedPost) {
                                                setSelectedPost(selectedTrackedPost);
                                            }
                                        }} posts={trackedPosts.map((t) => t.social_post)} postCollaborationMap={trackedPosts.reduce((acc: Record<string, Collaboration>, t) => {
                                            acc[t.social_post.uuid] = t.social_post.collaboration;
                                            return acc;
                                        }, {})} isLoading={initialLoading || loading} />
                                        {
                                            !(loading || initialLoading) && error &&
                                            <div className="d-flex justify-content-center align-items-center">
                                                <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                                            </div>
                                        }
                                    </InfiniteScroll>
                                    {
                                        !(loading || initialLoading) && (trackedPosts.length === 0) &&
                                        <div className="d-flex w-100 justify-content-center align-items-center">
                                            {
                                                (selectedSocialAccount?.track_media?.status === 'active') ? (
                                                    <div className="length-zero">
                                                        <h3>Currently there is no tracked post in your list, try adjusting filters!</h3>
                                                    </div>
                                                ) : (
                                                    <div className="length-zero d-flex flex-column gap-3">
                                                        <h3>Media tracking is not enabled for the selected account. Please enable tracking from social accounts page.</h3>
                                                        <Link className='btn btn-gradient d-flex gap-2 align-items-center' to={{ pathname: '/social-account' }}>View Social Accounts</Link>
                                                    </div>
                                                )
                                            }
                                        </div>
                                    }
                                </>
                            ) : (
                                <div className="d-flex w-100 justify-content-center align-items-center">
                                    <div className="length-zero d-flex flex-column gap-3">
                                        <h3>Currently you have not connected any social account!</h3>
                                        <Link className='btn btn-gradient d-flex gap-2 align-items-center' to={{ pathname: '/social-account' }} state={{ from: location.pathname + location.search, feature: 'comment_management' }}> <i className='mdi mdi-plus fs-5'></i> Add Account</Link>
                                    </div>
                                </div>
                            )
                        }

                    </div>
                }
                {
                    activeTab === 'creators' &&
                    <div className="campaign-page-content my-3">
                        {
                            (socialAccountId && selectedSocialAccount) ? (
                                <SocialAccountCreators socialAccountId={socialAccountId} />
                            ) : (
                                <div className="d-flex w-100 justify-content-center align-items-center">
                                    <div className="length-zero d-flex flex-column gap-3">
                                        <h3>Currently you have not connected any social account!</h3>
                                        <Link className='btn btn-gradient d-flex gap-2 align-items-center' to={{ pathname: '/social-account' }} state={{ from: location.pathname + location.search, feature: 'comment_management' }}> <i className='mdi mdi-plus fs-5'></i> Add Account</Link>
                                    </div>
                                </div>
                            )
                        }
                    </div>
                }

            </div>
            {
                selectedSocialAccount &&
                <>
                    <AddToCampaignOffcanvas
                        isActive={!!selectedPost}
                        onClose={() => setSelectedPost(null)}
                        post={selectedPost!}
                        onAddToCampaign={addToCampaign}
                    />
                </>
            }
        </>

    )
}

export default ifConfigured(TrackedPosts, 'agency_track_media', '/')
